/**
 * The chrome the Coast Guard forms without a bespoke card all share.
 *
 * Every one of them opens with the same read-only incident header and closes
 * with the same Prepared by box, so they get it from here rather than each
 * growing a copy. What sits between is the form's own business.
 */
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { OperationalPeriodScheduleControl } from '../OperationalPeriodScheduleControl';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import IcsPreparedByBox from '../IcsPreparedByBox';

export const INCIDENT_NAME_HINT =
  'This box is read-only. It is filled from the incident name.';

export const INCIDENT_LOCATION_HINT =
  'This box is read-only. It is filled from the incident location, falling back to the address set when the incident was created.';

export const OPERATIONAL_PERIOD_HINT =
  'Click this box to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';

type HeaderLabels = {
  incidentName: string;
  incidentLocation: string;
  operationalPeriod?: string;
};

/**
 * Boxes 1 to 3 as the sheet prints them: incident name, incident location and
 * the operational period window, all read-only.
 *
 * A form that dates itself rather than naming a period (the 236) omits the
 * third label and supplies its own box instead.
 */
export function NewCgHeaderBoxes({
  labels,
  testIdPrefix,
  trailing,
}: {
  labels: HeaderLabels;
  testIdPrefix: string;
  /** Rendered in place of box 3 for a form that has no operational period. */
  trailing?: ReactNode;
}) {
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  return (
    <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
      <Ics204CardSection
        boxLabel={labels.incidentName}
        testId={`${testIdPrefix}-section-incident-name`}
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the incident name">
            {INCIDENT_NAME_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value={incident.data?.name ?? ''}
          testId={`${testIdPrefix}-field-incident-name`}
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={labels.incidentLocation}
        testId={`${testIdPrefix}-section-incident-location`}
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the incident location">
            {INCIDENT_LOCATION_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value={incidentInfo?.incidentLocation || incident.data?.address || ''}
          testId={`${testIdPrefix}-field-incident-location`}
        />
      </Ics204CardSection>

      {labels.operationalPeriod ? (
        <Ics204CardSection
          boxLabel={labels.operationalPeriod}
          testId={`${testIdPrefix}-section-operational-period`}
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the operational period">
              {OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl
            testId={`${testIdPrefix}-field-operational-period`}
          />
        </Ics204CardSection>
      ) : (
        trailing
      )}
    </div>
  );
}

/**
 * The card shell: dark surface, title, export control and version banner.
 *
 * `boxes` are the numbered sections, which every form composes itself.
 */
export function NewCgFormCard({
  title,
  formname,
  baseId,
  exportLabel,
  children,
}: {
  title: string;
  formname: string;
  /**
   * The form's version chain. Left off by the 225, which holds one chain per
   * rated individual rather than one per period, so its banners belong to the
   * rows rather than to the card.
   */
  baseId?: string;
  exportLabel: string;
  children: ReactNode;
}) {
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();

  return (
    <Card className="dark tw:bg-background tw:text-foreground">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardAction>
          <FormExportButton
            formname={formname}
            label={exportLabel}
            disabled={isExporting}
            onExport={(format) =>
              void exportAsForm({
                formname,
                data: {},
                mapSketch: null,
                format,
              })
            }
            onExportExcel={
              formname === 'CG_5136' || formname === 'CG_5136X'
                ? () =>
                    void exportAsForm({
                      formname,
                      data: {},
                      mapSketch: null,
                      format: 'xlsx',
                    })
                : undefined
            }
          />
        </CardAction>
      </CardHeader>
      <CardContent className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
        {baseId ? <FormVersioningBanner baseId={baseId} /> : null}
        {children}
      </CardContent>
    </Card>
  );
}

/** The Prepared by box, wired to the form's version tip. */
export function NewCgPreparedByBox({
  baseId,
  boxLabel,
  testIdPrefix,
}: {
  baseId: string;
  boxLabel: string;
  testIdPrefix: string;
}) {
  const { effectivePeriodNumber } = useICSContext();
  return (
    <IcsPreparedByBox
      baseId={baseId}
      periodNumber={effectivePeriodNumber}
      boxLabel={boxLabel}
      testId={`${testIdPrefix}-section-prepared-by`}
    />
  );
}

export type BoxColumn = { readonly key: string; readonly label: string };

export type NamedRow = { readonly key: string; readonly label: string };

/**
 * A box whose rows are fixed by the printed sheet — named seats or nets — and
 * whose blanks live together in one JSON column rather than as rows of their
 * own.
 *
 * Rows cannot be added or removed, so each one only toggles between printed
 * text and an edit that saves explicitly.
 */
export function NamedRowEditTable({
  rowHeader,
  rows,
  blanks,
  values,
  onSave,
  testId,
}: {
  rowHeader: string;
  rows: readonly NamedRow[];
  blanks: readonly string[];
  values: Record<string, Record<string, string>>;
  onSave: (rowKey: string, next: Record<string, string>) => void;
  testId: string;
}) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const commit = (row: NamedRow) => {
    const current = values[row.key] ?? {};
    const next = { ...current };
    let changed = false;
    for (const blank of blanks) {
      const value = draft[blank] ?? '';
      if (value !== (current[blank] ?? '')) changed = true;
      next[blank] = value;
    }
    if (changed) onSave(row.key, next);
    setEditingKey(null);
  };

  return (
    <div className="tw:overflow-x-auto" data-testid={testId}>
      <table className="tw:w-full tw:text-sm">
        <thead>
          <tr>
            <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
              {rowHeader}
            </th>
            {blanks.map((blank) => (
              <th
                key={blank}
                className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground"
              >
                {blank}
              </th>
            ))}
            <th className="tw:w-24" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isEditing = editingKey === row.key;
            const current = values[row.key] ?? {};

            return (
              <tr key={row.key}>
                <td className="tw:px-2 tw:py-1 tw:align-top">{row.label}</td>
                {blanks.map((blank) => (
                  <td key={blank} className="tw:px-1 tw:py-1 tw:align-top">
                    {isEditing ? (
                      <Input
                        aria-label={`${row.label} ${blank}`}
                        value={draft[blank] ?? ''}
                        onChange={(event) =>
                          setDraft((state) => ({
                            ...state,
                            [blank]: event.target.value,
                          }))
                        }
                      />
                    ) : (
                      <span className="tw:block tw:px-2 tw:py-1 tw:break-words">
                        {current[blank] || '—'}
                      </span>
                    )}
                  </td>
                ))}
                <td className="tw:px-1 tw:py-1 tw:align-top">
                  <div className="tw:flex tw:justify-end tw:gap-1">
                    {isEditing ? (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Cancel editing ${row.label}`}
                          onClick={() => setEditingKey(null)}
                        >
                          <X className="tw:size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Save ${row.label}`}
                          onClick={() => commit(row)}
                        >
                          <Check className="tw:size-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${row.label}`}
                        onClick={() => {
                          setEditingKey(row.key);
                          setDraft(
                            Object.fromEntries(
                              blanks.map((blank) => [
                                blank,
                                current[blank] ?? '',
                              ])
                            )
                          );
                        }}
                      >
                        <Pencil className="tw:size-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The row table a numbered box wraps.
 *
 * Rows read as printed text until edited, so a filled form reads like the
 * sheet it prints rather than a wall of inputs. Each row edits on its own and
 * saves explicitly, and a row added but never saved is removed again on
 * Cancel rather than left behind as a blank line on the export.
 */
export function BoxRowTable({
  columns,
  rows,
  identityColumn,
  onSave,
  onDelete,
  onAdd,
  addLabel,
  testId,
}: {
  columns: readonly BoxColumn[];
  rows: Record<string, unknown>[];
  identityColumn: string;
  onSave: (entityId: string, patch: Record<string, string>) => void;
  onDelete: (entityId: string) => void;
  /** Adds a blank row and resolves to its id, which then opens for editing. */
  onAdd: () => Promise<string | undefined>;
  addLabel: string;
  testId: string;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [addedId, setAddedId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const rowId = (row: Record<string, unknown>) =>
    String(row[identityColumn] ?? row.id ?? '');

  const rowLabel = (row: Record<string, unknown>) => {
    const first = columns[0];
    return (first && String(row[first.key] ?? '').trim()) || 'this row';
  };

  const blankDraft = () =>
    Object.fromEntries(columns.map((column) => [column.key, '']));

  const beginEdit = (row: Record<string, unknown>) => {
    setEditingId(rowId(row));
    setDraft(
      Object.fromEntries(
        columns.map((column) => [column.key, String(row[column.key] ?? '')])
      )
    );
  };

  const cancelEdit = () => {
    if (editingId && editingId === addedId) onDelete(editingId);
    setAddedId(null);
    setEditingId(null);
  };

  const commitEdit = (row: Record<string, unknown>) => {
    const patch: Record<string, string> = {};
    for (const column of columns) {
      const next = draft[column.key] ?? '';
      if (next !== String(row[column.key] ?? '')) patch[column.key] = next;
    }
    if (Object.keys(patch).length > 0) onSave(rowId(row), patch);
    setAddedId(null);
    setEditingId(null);
  };

  const addRow = async () => {
    setIsAdding(true);
    try {
      const newId = await onAdd();
      if (!newId) return;
      setAddedId(newId);
      setEditingId(newId);
      setDraft(blankDraft());
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <div className="tw:flex tw:justify-start">
        <Button
          type="button"
          size="sm"
          disabled={isAdding}
          onClick={() => void addRow()}
        >
          <Plus className="tw:size-4" />
          {addLabel}
        </Button>
      </div>

      <div className="tw:overflow-x-auto" data-testid={testId}>
        <table className="tw:w-full tw:text-sm">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground"
                >
                  {column.label}
                </th>
              ))}
              <th className="tw:w-24" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const entityId = rowId(row);
              const isEditing = editingId === entityId;
              const label = rowLabel(row);

              return (
                <tr key={entityId}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="tw:px-1 tw:py-1 tw:align-top"
                    >
                      {isEditing ? (
                        <Input
                          aria-label={`${column.label} for ${label}`}
                          value={draft[column.key] ?? ''}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              [column.key]: event.target.value,
                            }))
                          }
                        />
                      ) : (
                        <span className="tw:block tw:px-2 tw:py-1 tw:break-words">
                          {String(row[column.key] ?? '') || '—'}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="tw:px-1 tw:py-1 tw:align-top">
                    <div className="tw:flex tw:justify-end tw:gap-1">
                      {isEditing ? (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Cancel editing ${label}`}
                            onClick={cancelEdit}
                          >
                            <X className="tw:size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Save ${label}`}
                            onClick={() => commitEdit(row)}
                          >
                            <Check className="tw:size-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${label}`}
                            onClick={() => beginEdit(row)}
                          >
                            <Pencil className="tw:size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete ${label}`}
                            onClick={() => onDelete(entityId)}
                          >
                            <Trash2 className="tw:size-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
